import { z } from 'zod';
import { languages } from '../enums.js';

/**
 * Centre d'Aide. Les articles sont des fichiers Markdown du front
 * (`apps/web/src/help/content/{fr,de,en}/{slug}.md`) ; leur liste fermée vit ici pour que le
 * serveur valide le comptage anonyme et que le front type ses liens « ? » : un article absent ne
 * compile pas.
 */

export const helpCategories = [
  'demarrage',
  'agenda',
  'patients',
  'consultations',
  'facturation',
  'retrocessions',
  'documents',
  'suivi',
  'doctena',
  'wero',
  'equipe',
  'abonnement',
  'securite',
  'faq',
] as const;
export type HelpCategory = (typeof helpCategories)[number];

export const helpArticleSlugs = [
  // Démarrage
  'bienvenue',
  'identite-cabinet',
  'identifiants-legaux',
  'regime-tva',
  // Agenda
  'horaires-absences',
  'rendez-vous',
  'recurrences-post-it',
  'types-consultation-tarifs',
  // Patients
  'creer-dossier',
  'securite-sociale-frontalier',
  'import-patients',
  'doublons-fusion',
  // Consultations
  'premiere-consultation',
  'schema-tests-questionnaires',
  'bilan-pediatrique',
  'courriers-ia',
  // Facturation et relances
  'premiere-facture',
  'coordonnees-bancaires',
  'encaisser-paiement',
  'avoir',
  'facture-par-courriel',
  'relances-interets',
  'ordonnance-paiement',
  'export-comptable',
  // Rétrocessions
  'retrocessions',
  // Documents et scanner
  'documents-dossier',
  'scanner-telephone',
  // Suivi patient
  'suivi-avis',
  'rappels-rendez-vous',
  'reservation-inscription',
  // Intégrations
  'connecter-doctena',
  'wero-buckaroo',
  // Équipe et rôles
  'inviter-equipe',
  'transfert-propriete',
  // Abonnement et données
  'essai-abonnement',
  'export-donnees',
  // RGPD et sécurité
  'securite-donnees',
  // Questions fréquentes
  'faq-hosting',
  'faq-access',
  'faq-rgpd',
  'faq-ai',
  'faq-trial',
  'faq-plan-change',
  'faq-record',
  'faq-sticky',
  'faq-motif',
  'faq-body-chart',
  'faq-one-consultation',
  'faq-import',
  'faq-reminders',
  'faq-doctena',
  'faq-invoice-session',
  'faq-invoiced-mark',
  'faq-wero',
  'faq-languages',
  'faq-password',
  'faq-two-factor',
  'faq-owner-settings',
  'faq-support',
] as const;
export type HelpArticleSlug = (typeof helpArticleSlugs)[number];

export function isHelpArticleSlug(value: string): value is HelpArticleSlug {
  return (helpArticleSlugs as readonly string[]).includes(value);
}

export const helpEventKinds = ['view', 'search_miss'] as const;
export type HelpEventKind = (typeof helpEventKinds)[number];

/**
 * Comptage anonyme (décision de Nicolas, 11 octobre 2026) : par article, langue et jour, **sans
 * utilisateur, sans cabinet, sans le texte cherché**. Une lecture nomme son article ; une recherche
 * sans résultat n'en nomme aucun et ne transporte rien de ce qui a été tapé.
 */
export const helpEventSchema = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('view'), language: z.enum(languages), article: z.enum(helpArticleSlugs) }).strict(),
    z.object({ kind: z.literal('search_miss'), language: z.enum(languages) }).strict(),
  ]);
export type HelpEventInput = z.infer<typeof helpEventSchema>;

export const helpStatsQuerySchema = z
  .object({ days: z.coerce.number().int().min(1).max(365).default(30) })
  .strict();

export interface HelpArticleStatDto {
  article: HelpArticleSlug;
  views: number;
}

export interface HelpStatsDto {
  days: number;
  /** Par article, toutes langues confondues, du plus lu au moins lu. */
  articles: HelpArticleStatDto[];
  /** Lectures et recherches sans résultat par langue. */
  byLanguage: { language: (typeof languages)[number]; views: number; searchMisses: number }[];
  searchMisses: number;
}
